import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { subscriptionsAPI } from '@/services/api';
import { toast } from 'sonner';
import { Check, Loader2, Sparkles } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { db } from '@/config/firebase';
import { collection, getDocs } from 'firebase/firestore';

const SubscriptionDialog = ({ open, onClose, onSuccess }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const { userProfile } = useAuth();

  useEffect(() => {
    if (open) {
      fetchPlans();
    }
  }, [open]);

  const fetchPlans = async () => {
    try {
      // Read plans directly from Firestore
      const plansRef = collection(db, 'plans');
      const snapshot = await getDocs(plansRef);
      const plansList = [];
      snapshot.forEach((doc) => {
        plansList.push({ id: doc.id, ...doc.data() });
      });
      // Sort plans by price (lowest to highest)
      plansList.sort((a, b) => a.price - b.price);
      setPlans(plansList);
    } catch (error) {
      console.error('Error fetching plans:', error);
      toast.error('Failed to load subscription plans');
    }
  };

  const handleSubscribe = async (planId) => {
    if (planId === 'free') {
      toast.info('You are already on the free plan');
      return;
    }

    setLoading(true);
    setSelectedPlan(planId);

    try {
      const response = await subscriptionsAPI.subscribe(planId);
      if (response.data?.payment?.mock) {
        toast.warning('Mock payment - PayPlus integration pending');
      }
      toast.success('Subscription activated successfully!');
      onSuccess();
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error(error.message || 'Failed to subscribe');
    } finally {
      setLoading(false);
      setSelectedPlan(null);
    }
  };

  const currentPlan = userProfile?.subscription?.plan || 'free';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="subscription-dialog">
        <DialogHeader>
          <DialogTitle className="text-3xl font-bold text-center">
            Choose Your Plan
          </DialogTitle>
          <DialogDescription className="text-center text-lg">
            Unlock more credits and create unlimited designs
          </DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          {plans.map((plan) => {
            const isCurrentPlan = plan.id === currentPlan;
            const isPopular = plan.id === 'pro';

            return (
              <Card
                key={plan.id}
                className={`relative ${isCurrentPlan ? 'ring-2 ring-cyan-500' : ''} ${isPopular ? 'border-cyan-500 border-2' : ''}`}
                data-testid={`plan-${plan.id}`}
              >
                {isPopular && (
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-gradient-to-r from-cyan-600 to-turquoise-600">
                      <Sparkles className="w-3 h-3 mr-1" />
                      Popular
                    </Badge>
                  </div>
                )}
                {isCurrentPlan && (
                  <div className="absolute -top-3 right-4">
                    <Badge variant="outline" className="bg-white">
                      Current
                    </Badge>
                  </div>
                )}

                <CardHeader>
                  <CardTitle className="text-2xl">{plan.name}</CardTitle>
                  <CardDescription>
                    <span className="text-3xl font-bold text-gray-900">
                      ${plan.price}
                    </span>
                    {plan.price > 0 && <span className="text-gray-600">/month</span>}
                  </CardDescription>
                </CardHeader>

                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <Check className="w-4 h-4 text-green-600" />
                      <span className="text-sm">{plan.credits} credits/month</span>
                    </div>
                  </div>

                  <Button
                    className={`w-full ${
                      isPopular
                        ? 'bg-gradient-to-r from-cyan-600 to-turquoise-600 hover:from-cyan-700 hover:to-turquoise-700'
                        : ''
                    }`}
                    onClick={() => handleSubscribe(plan.id)}
                    disabled={loading || isCurrentPlan}
                    data-testid={`subscribe-${plan.id}-button`}
                  >
                    {loading && selectedPlan === plan.id ? (
                      <Loader2 className="animate-spin" />
                    ) : isCurrentPlan ? (
                      'Current Plan'
                    ) : plan.id === 'free' ? (
                      'Free Forever'
                    ) : (
                      'Subscribe'
                    )}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        <div className="mt-6 text-center text-sm text-gray-500">
          <p>All plans include access to our AI-powered design tools</p>
          <p className="mt-1">Cancel anytime, no questions asked</p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SubscriptionDialog;